import React, { useEffect, useRef, useState } from "react";

import { ScheduleItem } from "../data/creatorsChoiceData";
import { usePseudoLive } from "../hooks/usePseudoLive";

interface Props {
  channelId: string | null;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  onTrackChange: (
    track: ScheduleItem | null,
    nextTrack: ScheduleItem | null,
  ) => void;
  onTimeUpdate: (currentTime: number, duration: number) => void;
  onError: (error: string | null) => void;
  onPlayStateChange: (isPlaying: boolean) => void;
}

export const CreatorAudioEngine: React.FC<Props> = ({
  channelId,
  isPlaying,
  volume,
  isMuted,
  onTrackChange,
  onTimeUpdate,
  onError,
  onPlayStateChange: _onPlayStateChange,
}) => {
  // 1. Engine - Logic
  const {
    currentTrack,
    nextTrack,
    startOffset,
    error,
    forceSync,
    timeUntilNext: _timeUntilNext,
  } = usePseudoLive(channelId || null);

  // 2. Audio Elements (Dual Deck)
  const deckARef = useRef<HTMLAudioElement>(null);
  const deckBRef = useRef<HTMLAudioElement>(null);

  // We also need a Jingle player that sits on top
  const overlayRef = useRef<HTMLAudioElement>(null);

  // 3. State
  // Active Deck: 'A' or 'B'. The "Primary" deck playing the `currentTrack`.
  // We flip this when the track changes.
  const [activeDeck, setActiveDeck] = useState<"A" | "B">("A");

  // Track references to know what's loaded where
  const deckATrackId = useRef<string | null>(null);
  const deckBTrackId = useRef<string | null>(null);

  // Overlay State
  const [playedOverlays, setPlayedOverlays] = useState<Set<number>>(new Set());
  const [activeOverlayType, setActiveOverlayType] = useState<
    "intro" | "outro" | "voiceover_overlap" | "music_bed" | null
  >(null);

  // Notify parent on track changes
  useEffect(() => {
    onTrackChange(currentTrack, nextTrack);
    if (error) onError(error);
  }, [currentTrack, nextTrack, error, onTrackChange, onError]);

  // --- DUAL DECK SYNC LOGIC ---
  useEffect(() => {
    const deckA = deckARef.current;
    const deckB = deckBRef.current;
    if (!deckA || !deckB || !currentTrack) return;

    // Determine which deck should play CURRENT track
    // If activeDeck is A, Deck A is Current, Deck B is Next (or Empty)
    // If activeDeck is B, Deck B is Current, Deck A is Next (or Empty)

    const primaryDeck = activeDeck === "A" ? deckA : deckB;
    const secondaryDeck = activeDeck === "A" ? deckB : deckA;
    const primaryIdRef = activeDeck === "A" ? deckATrackId : deckBTrackId;
    const secondaryIdRef = activeDeck === "A" ? deckBTrackId : deckATrackId;

    // 1. SYNC PRIMARY DECK (Current Track)
    if (primaryIdRef.current !== currentTrack.track.id) {
      console.log(
        `[Engine] Loading PRIMARY (${activeDeck}): ${currentTrack.track.title}`,
      );
      primaryDeck.src = currentTrack.track.url;
      primaryDeck.volume = isMuted ? 0 : volume;
      primaryIdRef.current = currentTrack.track.id;

      // Initial Seek
      const seekTime = startOffset;
      primaryDeck.currentTime = seekTime;

      if (isPlaying)
        primaryDeck
          .play()
          .catch((e: unknown) => console.warn("Primary Play failed", e));
    }

    // 2. PRELOAD SECONDARY DECK (Next Track)
    // We preload if we are within 30s of end (heuristic) OR if there is an overlap scheduled
    // Crossfade logic relies on `timeUntilNext`
    if (nextTrack && secondaryIdRef.current !== nextTrack.track.id) {
      console.log(`[Engine] Preloading SECONDARY: ${nextTrack.track.title}`);
      secondaryDeck.src = nextTrack.track.url;
      secondaryDeck.volume = 0; // Start silent
      secondaryIdRef.current = nextTrack.track.id;
      // Don't play yet, wait for crossfade trigger
      // But valid seek might be needed if we switched Mid-Show?
      // Usually next track starts at 0.
      secondaryDeck.currentTime = 0;
    }
  }, [
    currentTrack,
    nextTrack,
    activeDeck,
    startOffset,
    isPlaying,
    volume,
    isMuted,
  ]);

  // --- PLAY/PAUSE CONTROL ---
  useEffect(() => {
    const deckA = deckARef.current;
    const deckB = deckBRef.current;
    if (!deckA || !deckB) return;

    if (isPlaying) {
      // Play the active deck.
      // The secondary deck is managed by Crossfade Logic (it might need to play if overlapping)
      if (activeDeck === "A" && deckA.paused)
        deckA.play().catch((e: unknown) => console.warn(e));
      if (activeDeck === "B" && deckB.paused)
        deckB.play().catch((e: unknown) => console.warn(e));
    } else {
      deckA.pause();
      deckB.pause();
      if (overlayRef.current) overlayRef.current.pause();
    }
  }, [isPlaying, activeDeck]);

  // --- CROSSFADE & VOLUME AUTOMATION LOOP ---
  useEffect(() => {
    const deckA = deckARef.current;
    const deckB = deckBRef.current;
    if (!deckA || !deckB) return;

    const interval = setInterval(() => {
      if (!isPlaying || isMuted) return;

      const primaryDeck = activeDeck === "A" ? deckA : deckB;
      const secondaryDeck = activeDeck === "A" ? deckB : deckA;

      // Check if Primary ended (Force Sync)
      if (primaryDeck.ended) {
        console.log("[Engine] Primary Ended - Force Next");
        forceSync();
        // Toggle Deck Logic handled by usePseudoLive:
        // When forceSync runs, `usePseudoLive` updates `currentTrack` to be the Next Item.
        // We detect this change:
        // If `currentTrack` matches `secondaryIdRef`, we assume the transition happened.
        // WE MUST FLIP THE DECK.
        return;
      }

      // CROSSFADE LOGIC
      const overlapDuration = currentTrack?.crossfadeDuration || 0;

      // If overlap > 0, we need to start Secondary Deck early
      if (overlapDuration > 0 && nextTrack) {
        // Calculate time remaining on Primary
        // BUG FIX: Must use VIRTUAL duration for sliced segments, not file duration.
        if (primaryDeck.duration) {
          const trackStart = currentTrack.sourceOffset || 0;
          const trackDuration = currentTrack.duration || primaryDeck.duration;
          const virtualEndTime = trackStart + trackDuration;

          const remaining = virtualEndTime - primaryDeck.currentTime;

          // Debug logs to help diagnose
          // console.log(`[Engine] Rem: ${remaining.toFixed(1)}s (VirtEnd: ${virtualEndTime}, Curr: ${primaryDeck.currentTime.toFixed(1)})`);

          if (remaining <= overlapDuration) {
            // START SECONDARY if paused
            if (secondaryDeck.paused) {
              console.log(
                `[Engine] Starting Crossfade (Rem: ${remaining.toFixed(1)}s) - Playing Secondary`,
              );
              secondaryDeck.play().catch((e: unknown) => console.warn(e));
            }

            // CALCULATE VOLUMES
            // Linear Crossfade? Or "Bed" Logic?
            // User said: "Song starts quietly (bed) ... fade to 0.2 ... then when podcast ends, fade to 1.0"
            // This implies DIFFERENT curves for Pod->Song vs Song->Pod.

            // Scenario A: Pod -> Song
            if (
              currentTrack?.track.type === "podcast" &&
              nextTrack.track.type === "music"
            ) {
              const progress = 1 - remaining / overlapDuration; // 0 to 1
              primaryDeck.volume = volume; // Keep Pod Full
              secondaryDeck.volume = volume * 0.2 * progress; // Ramp to 20%
            }
            // Scenario B: Song -> Pod
            else if (
              currentTrack?.track.type === "music" &&
              nextTrack.track.type === "podcast"
            ) {
              const progress = 1 - remaining / overlapDuration;
              primaryDeck.volume = volume * (1 - progress); // Fade Out
              secondaryDeck.volume = volume * progress; // Fade In
            }
            // Scenario C: Generic
            else {
              const progress = 1 - remaining / overlapDuration;
              primaryDeck.volume = volume * (1 - progress);
              secondaryDeck.volume = volume * progress;
            }
          } else {
            // Not in crossfade zone
            // Ensure Primary is Full
            // EXCEPT if overlay active (ducking handled below)
            if (!activeOverlayType) {
              primaryDeck.volume = volume;
            }
          }
        }
      } else {
        // No crossfade configured
        if (!activeOverlayType) primaryDeck.volume = volume;
      }

      // DUCKING LOGIC (Overlays on Primary)
      // If an overlay is active (e.g. Jingle), duck the Primary
      // Note: If we are in Crossfade, we might have weird interactions.
      // Let's assume overlay logic applies to whoever is loudest?
      // Usually overlays are on the MUSIC track.

      // "Jingles ... during songs"
      // If Primary is Song, apply ducking logic.
      if (
        activeOverlayType === "voiceover_overlap" ||
        activeOverlayType === "intro"
      ) {
        if (currentTrack?.track.type === "music") {
          primaryDeck.volume = volume * 0.2; // Duck
        }
      } else if (activeOverlayType === "music_bed") {
        // This is legacy logic for "Overlay as Bed", but we are moving to "Track B is Bed".
        // If using old overlay logic for beds, handle it:
        if (overlayRef.current) overlayRef.current.volume = volume * 0.2;
        if (currentTrack?.track.type === "podcast") primaryDeck.volume = volume;
      }
    }, 100); // 10Hz control loop

    return () => clearInterval(interval);
  }, [
    isPlaying,
    volume,
    activeOverlayType,
    currentTrack,
    nextTrack,
    activeDeck,
    isMuted,
    forceSync,
  ]);

  // --- DECK FLIPPING ---
  // Detect when currentTrack changes ID and update ActiveDeck
  useEffect(() => {
    // If the NEW current track matches what we had in Deck B, then B becomes Primary.
    if (currentTrack && currentTrack.track.id === deckBTrackId.current) {
      console.log("[Engine] FLIP -> Deck B is now Primary");
      setTimeout(() => setActiveDeck("B"), 0);
      // Ensure volume is restored if it was faded
      if (deckBRef.current) deckBRef.current.volume = volume;

      // Allow Deck A to be reused for NEXT
      deckATrackId.current = null;
    } else if (currentTrack && currentTrack.track.id === deckATrackId.current) {
      console.log("[Engine] FLIP -> Deck A is now Primary");
      setTimeout(() => setActiveDeck("A"), 0);
      if (deckARef.current) deckARef.current.volume = volume;
      deckBTrackId.current = null;
    }
  }, [currentTrack, volume]);

  // --- SYNC TIME UPDATE ---
  useEffect(() => {
    const deckA = deckARef.current;
    const deckB = deckBRef.current;
    const handleTimeUpdate = () => {
      // We report the time of the PRIMARY deck
      const primary = activeDeck === "A" ? deckA : deckB;
      if (primary && primary.duration) {
        // For UI progress, we want "Time Elapsed in Segment" not "File Position"
        // Segment Progress = currentTime - sourceOffset
        const relativeTime =
          primary.currentTime - (currentTrack?.sourceOffset || 0);
        const segmentDuration = currentTrack?.duration || primary.duration;

        // However, the Parent usually expects 0..1 or absolute.
        // Let's report relative values because the Progress Bar should show "Song Progress".
        onTimeUpdate(Math.max(0, relativeTime), segmentDuration);

        checkOverlays(primary.currentTime);
      }
    };

    const checkOverlays = (currentTime: number) => {
      if (!currentTrack || !currentTrack.overlays || !overlayRef.current)
        return;

      // Calculate Relative Time for Overlay Triggers
      const relativeTime = currentTime - (currentTrack.sourceOffset || 0);

      currentTrack.overlays.forEach((overlay, index) => {
        if (playedOverlays.has(index)) return;
        // Don't play music_bed overlay if we are using Crossfade Bed logic?
        // Actually, buildDubSpecificSchedule USES 'music_bed' overlay type for the bed inside the pod.
        // So we MUST support it.

        // TRIGGER check using RELATIVE time
        if (
          relativeTime >= overlay.startAt &&
          relativeTime < overlay.startAt + 1
        ) {
          console.log(`[Engine] Trigger Overlay: ${overlay.type}`);
          const ovAudio = overlayRef.current!;
          ovAudio.src = overlay.url;
          ovAudio.volume = overlay.type === "music_bed" ? volume * 0.2 : volume;
          ovAudio.currentTime = overlay.offset || 0;
          ovAudio.play().catch((e: unknown) => console.warn(e));
          setActiveOverlayType(overlay.type);
          setPlayedOverlays((prev) => new Set(prev).add(index));

          const stopTime = overlay.duration ? overlay.duration * 1000 : 0;
          if (stopTime) {
            setTimeout(() => {
              setActiveOverlayType(null);
              // Fade out overlay?
            }, stopTime);
          } else {
            ovAudio.onended = () => setActiveOverlayType(null);
          }
        }
      });
    };

    if (deckA) deckA.addEventListener("timeupdate", handleTimeUpdate);
    if (deckB) deckB.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      if (deckA) deckA.removeEventListener("timeupdate", handleTimeUpdate);
      if (deckB) deckB.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [activeDeck, currentTrack, playedOverlays, volume, onTimeUpdate]);

  return (
    <div style={{ display: "none" }}>
      <audio ref={deckARef} />
      <audio ref={deckBRef} />
      <audio ref={overlayRef} />
    </div>
  );
};
